/**
 * Editor de Pesquisa - Criar/Editar pesquisas e perguntas
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../../services/staticDataConfig';

type TipoPergunta = 'texto_curto' | 'texto_longo' | 'multipla_escolha' | 'caixas_selecao' | 'escala_linear' | 'data' | 'email';

interface Opcao {
  id?: number;
  texto: string;
  ordem: number;
}

interface Pergunta {
  id?: number;
  titulo: string;
  descricao?: string;
  tipo: TipoPergunta;
  obrigatoria: boolean;
  ordem: number;
  config?: any;
  opcoes: Opcao[];
  editando?: boolean;
}

const TIPOS_PERGUNTA: { value: TipoPergunta; label: string; icon: string }[] = [
  { value: 'texto_curto', label: 'Texto Curto', icon: 'fa-font' },
  { value: 'texto_longo', label: 'Texto Longo', icon: 'fa-align-left' },
  { value: 'multipla_escolha', label: 'Múltipla Escolha', icon: 'fa-dot-circle' },
  { value: 'caixas_selecao', label: 'Caixas de Seleção', icon: 'fa-check-square' },
  { value: 'escala_linear', label: 'Escala Linear', icon: 'fa-sliders-h' },
  { value: 'data', label: 'Data', icon: 'fa-calendar' },
  { value: 'email', label: 'Email', icon: 'fa-envelope' }
];

export function EditorPesquisa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  
  const isEdicao = Boolean(id);
  
  // Estado da pesquisa
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativa, setAtiva] = useState(true);
  const [permiteMult, setPermiteMult] = useState(false);
  const [requerLogin, setRequerLogin] = useState(false);
  const [exibeResultados, setExibeResultados] = useState(false);
  const [mensagemAgradecimento, setMensagemAgradecimento] = useState('');
  const [linkPublico, setLinkPublico] = useState('');
  
  // Estado das perguntas
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  
  // Estado UI
  const [loading, setLoading] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [perguntaEditando, setPerguntaEditando] = useState<number | null>(null);

  const carregarPesquisa = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const pesquisa = data.dados;
        
        setTitulo(pesquisa.titulo);
        setDescricao(pesquisa.descricao || '');
        setAtiva(pesquisa.ativa);
        setPermiteMult(pesquisa.permite_multiplas_respostas);
        setRequerLogin(pesquisa.requer_login);
        setExibeResultados(pesquisa.exibe_resultados);
        setMensagemAgradecimento(pesquisa.mensagem_agradecimento || '');
        setLinkPublico(pesquisa.link_publico);
      }
    } catch (error) {
      console.error('Erro ao carregar pesquisa:', error);
    }
  }, [id, token]);

  const carregarPerguntas = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/perguntas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPerguntas(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (isEdicao) {
      carregarPesquisa();
      carregarPerguntas();
    }
  }, [isEdicao, carregarPesquisa, carregarPerguntas]);

  const salvarPesquisa = async () => {
    if (!titulo.trim()) {
      alert('Digite um título para a pesquisa');
      return;
    }

    setSalvando(true);

    try {
      const dados = {
        titulo,
        descricao,
        ativa,
        permite_multiplas_respostas: permiteMult,
        requer_login: requerLogin,
        exibe_resultados: exibeResultados,
        mensagem_agradecimento: mensagemAgradecimento
      };

      if (isEdicao) {
        const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dados)
        });

        if (response.ok) {
          alert('Pesquisa atualizada com sucesso!');
        } else {
          throw new Error('Erro ao atualizar');
        }
      } else {
        const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dados)
        });

        if (response.ok) {
          const result = await response.json();
          alert('Pesquisa criada com sucesso!');
          navigate(`/admin/pesquisas/${result.pesquisaId}/editar`);
        } else {
          throw new Error('Erro ao criar');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar pesquisa');
    } finally {
      setSalvando(false);
    }
  };

  const adicionarPergunta = () => {
    const novaPergunta: Pergunta = {
      titulo: 'Nova pergunta',
      tipo: 'texto_curto',
      obrigatoria: false,
      ordem: perguntas.length,
      config: {},
      opcoes: [],
      editando: true
    };
    setPerguntas([...perguntas, novaPergunta]);
    setPerguntaEditando(perguntas.length);
  };

  const salvarPergunta = async (index: number) => {
    const pergunta = perguntas[index];
    
    if (!pergunta.titulo.trim()) {
      alert('Digite um título para a pergunta');
      return;
    }

    try {
      const configNormalizada = (() => {
        if (pergunta.tipo !== 'escala_linear') {
          return pergunta.config;
        }

        const minRaw = Number.isFinite(pergunta.config?.min) ? Number(pergunta.config.min) : 1;
        const maxRaw = Number.isFinite(pergunta.config?.max) ? Number(pergunta.config.max) : 10;
        const min = Math.floor(minRaw);
        const max = Math.floor(maxRaw);

        const minFinal = Number.isFinite(min) ? min : 1;
        const maxFinal = Number.isFinite(max) ? max : 10;

        return {
          ...pergunta.config,
          min: Math.min(minFinal, maxFinal),
          max: Math.max(minFinal, maxFinal),
          label_min: pergunta.config?.label_min || '',
          label_max: pergunta.config?.label_max || ''
        };
      })();

      const dados = {
        titulo: pergunta.titulo,
        descricao: pergunta.descricao,
        tipo: pergunta.tipo,
        obrigatoria: pergunta.obrigatoria,
        ordem: pergunta.ordem,
        config: configNormalizada,
        opcoes: pergunta.opcoes.map((o, i) => ({ texto: o.texto, ordem: i }))
      };

      if (pergunta.id) {
        // Atualizar
        await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/perguntas/${pergunta.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dados)
        });
      } else {
        // Criar
        const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/perguntas`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dados)
        });

        if (response.ok) {
          const result = await response.json();
          const novasPerguntas = [...perguntas];
          novasPerguntas[index] = { ...pergunta, id: result.perguntaId, editando: false };
          setPerguntas(novasPerguntas);
        }
      }

      setPerguntaEditando(null);
      carregarPerguntas();
    } catch (error) {
      console.error('Erro ao salvar pergunta:', error);
      alert('Erro ao salvar pergunta');
    }
  };

  const excluirPergunta = async (index: number) => {
    const pergunta = perguntas[index];
    
    if (pergunta.id) {
      if (!confirm('Excluir esta pergunta?')) return;
      
      try {
        await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/perguntas/${pergunta.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Erro ao excluir:', error);
      }
    }

    const novas = perguntas.filter((_, i) => i !== index);
    setPerguntas(novas);
    setPerguntaEditando(null);
  };

  const atualizarPergunta = (index: number, campo: keyof Pergunta, valor: any) => {
    const novas = [...perguntas];
    novas[index] = { ...novas[index], [campo]: valor };
    setPerguntas(novas);
  };

  // Função para atualizar múltiplos campos de uma vez (evita race condition)
  const atualizarPerguntaMultiplo = (index: number, campos: Partial<Pergunta>) => {
    setPerguntas(prev => {
      const novas = [...prev];
      novas[index] = { ...novas[index], ...campos };
      return novas;
    });
  };

  const adicionarOpcao = (perguntaIndex: number) => {
    const novas = [...perguntas];
    const novaOpcao: Opcao = {
      texto: `Opção ${novas[perguntaIndex].opcoes.length + 1}`,
      ordem: novas[perguntaIndex].opcoes.length
    };
    novas[perguntaIndex].opcoes = [...novas[perguntaIndex].opcoes, novaOpcao];
    setPerguntas(novas);
  };

  const atualizarOpcao = (perguntaIndex: number, opcaoIndex: number, texto: string) => {
    const novas = [...perguntas];
    novas[perguntaIndex].opcoes[opcaoIndex].texto = texto;
    setPerguntas(novas);
  };

  const removerOpcao = (perguntaIndex: number, opcaoIndex: number) => {
    const novas = [...perguntas];
    novas[perguntaIndex].opcoes = novas[perguntaIndex].opcoes.filter((_, i) => i !== opcaoIndex);
    setPerguntas(novas);
  };

  const copiarLink = () => {
    const url = `${window.location.origin}/pesquisa/${linkPublico}`;
    navigator.clipboard.writeText(url);
    alert('Link copiado!');
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center p-5">
          <div className="spinner-border text-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">
          <i className="fas fa-edit me-2"></i>
          {isEdicao ? 'Editar Pesquisa' : 'Nova Pesquisa'}
        </h2>
        <div>
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/admin/pesquisas')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Voltar
          </button>
          <button
            className="btn btn-success"
            onClick={salvarPesquisa}
            disabled={salvando}
          >
            <i className="fas fa-save me-2"></i>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

          {/* Configurações da Pesquisa */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-cog me-2"></i>
                Configurações
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Título *</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={titulo}
                      onChange={(e) => setTitulo(e.target.value)}
                      placeholder="Digite o título da pesquisa"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  {linkPublico && (
                    <div className="mb-3">
                      <label className="form-label">Link Público</label>
                      <div className="input-group">
                        <input 
                          type="text"
                          className="form-control"
                          value={`/pesquisa/${linkPublico}`}
                          readOnly
                        />
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={copiarLink}
                          title="Copiar link"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Descrição</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Descrição opcional que aparecerá no início da pesquisa"
                />
              </div>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="ativa"
                      checked={ativa}
                      onChange={(e) => setAtiva(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="ativa">
                      Pesquisa ativa
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="permiteMult"
                      checked={permiteMult}
                      onChange={(e) => setPermiteMult(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="permiteMult">
                      Múltiplas respostas
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="requerLogin"
                      checked={requerLogin}
                      onChange={(e) => setRequerLogin(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="requerLogin">
                      Requer login
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="exibeResultados"
                      checked={exibeResultados}
                      onChange={(e) => setExibeResultados(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="exibeResultados">
                      Exibir resultados
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label">Mensagem de Agradecimento</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={mensagemAgradecimento}
                  onChange={(e) => setMensagemAgradecimento(e.target.value)}
                  placeholder="Mensagem exibida após o envio das respostas"
                />
              </div>
            </div>
          </div>

          {/* Perguntas */}
          {isEdicao && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0">
                  <i className="fas fa-list-ol me-2"></i>
                  Perguntas ({perguntas.length})
                </h3>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={adicionarPergunta}
                >
                  <i className="fas fa-plus me-2"></i>
                  Adicionar Pergunta
                </button>
              </div>
              <div className="card-body">
                {perguntas.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-list fa-3x mb-3"></i>
                    <p>Nenhuma pergunta adicionada</p>
                    <button 
                      className="btn btn-primary"
                      onClick={adicionarPergunta}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Adicionar Primeira Pergunta
                    </button>
                  </div>
                ) : (
                  <div className="list-group">
                    {perguntas.map((pergunta, index) => (
                      <div key={pergunta.id || index} className="list-group-item mb-2 border rounded" style={{ overflow: 'visible' }}>
                        {perguntaEditando === index ? (
                          // Modo edição
                          <div>
                            <div className="mb-3">
                              <label className="form-label">Título da Pergunta *</label>
                              <input 
                                type="text"
                                className="form-control"
                                value={pergunta.titulo}
                                onChange={(e) => atualizarPergunta(index, 'titulo', e.target.value)}
                                placeholder="Digite a pergunta"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Descrição (opcional)</label>
                              <input 
                                type="text"
                                className="form-control"
                                value={pergunta.descricao || ''}
                                onChange={(e) => atualizarPergunta(index, 'descricao', e.target.value)}
                                placeholder="Adicione uma descrição ou instrução"
                              />
                            </div>

                            <div className="row mb-3">
                              <div className="col-12">
                                <label className="form-label">Tipo de Pergunta</label>
                                <div className="d-flex flex-wrap gap-2">
                                  {TIPOS_PERGUNTA.map(tipo => (
                                    <button
                                      key={tipo.value}
                                      type="button"
                                      style={{ minWidth: '130px' }}
                                      className={`btn btn-sm ${pergunta.tipo === tipo.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                                      onClick={() => {
                                        const novoTipo = tipo.value;
                                        
                                        // Preparar todas as atualizações em um único objeto
                                        const atualizacoes: Partial<Pergunta> = {
                                          tipo: novoTipo
                                        };
                                        
                                        // Limpar opções se mudar para tipo sem opções
                                        if (!['multipla_escolha', 'caixas_selecao'].includes(novoTipo)) {
                                          atualizacoes.opcoes = [];
                                        }

                                        // Garantir config padrão para escala linear
                                        if (novoTipo === 'escala_linear') {
                                          atualizacoes.config = {
                                            min: Number.isFinite(pergunta.config?.min) ? pergunta.config.min : 1,
                                            max: Number.isFinite(pergunta.config?.max) ? pergunta.config.max : 10,
                                            label_min: pergunta.config?.label_min || '',
                                            label_max: pergunta.config?.label_max || ''
                                          };
                                        }
                                        
                                        // Atualizar tudo de uma vez para evitar race condition
                                        atualizarPerguntaMultiplo(index, atualizacoes);
                                      }}
                                    >
                                      <i className={`fas ${tipo.icon} me-1`}></i>
                                      {tipo.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="row mb-3">
                              <div className="col-md-6 d-flex align-items-end">
                                <div className="form-check">
                                  <input 
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`obrigatoria-${index}`}
                                    checked={pergunta.obrigatoria}
                                    onChange={(e) => atualizarPergunta(index, 'obrigatoria', e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor={`obrigatoria-${index}`}>
                                    Pergunta obrigatória
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Configurações específicas por tipo */}
                            {pergunta.tipo === 'escala_linear' && (
                              <div className="row mb-3">
                                <div className="col-md-3">
                                  <label className="form-label">Mínimo</label>
                                  <input 
                                    type="number"
                                    className="form-control"
                                    value={pergunta.config?.min || 1}
                                    onChange={(e) => atualizarPergunta(index, 'config', {
                                      ...pergunta.config,
                                      min: Number.parseInt(e.target.value || '1', 10)
                                    })}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Máximo</label>
                                  <input 
                                    type="number"
                                    className="form-control"
                                    value={pergunta.config?.max || 10}
                                    onChange={(e) => atualizarPergunta(index, 'config', {
                                      ...pergunta.config,
                                      max: Number.parseInt(e.target.value || '10', 10)
                                    })}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Label Mínimo</label>
                                  <input 
                                    type="text"
                                    className="form-control"
                                    value={pergunta.config?.label_min || ''}
                                    onChange={(e) => atualizarPergunta(index, 'config', {
                                      ...pergunta.config,
                                      label_min: e.target.value
                                    })}
                                    placeholder="Ex: Ruim"
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Label Máximo</label>
                                  <input 
                                    type="text"
                                    className="form-control"
                                    value={pergunta.config?.label_max || ''}
                                    onChange={(e) => atualizarPergunta(index, 'config', {
                                      ...pergunta.config,
                                      label_max: e.target.value
                                    })}
                                    placeholder="Ex: Excelente"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Opções para múltipla escolha e checkbox */}
                            {['multipla_escolha', 'caixas_selecao'].includes(pergunta.tipo) && (
                              <div className="mb-3">
                                <label className="form-label">Opções</label>
                                {pergunta.opcoes.map((opcao, opcaoIndex) => (
                                  <div key={opcaoIndex} className="input-group mb-2">
                                    <span className="input-group-text">
                                      <i className={`fas ${pergunta.tipo === 'multipla_escolha' ? 'fa-circle' : 'fa-square'}`}></i>
                                    </span>
                                    <input 
                                      type="text"
                                      className="form-control"
                                      value={opcao.texto}
                                      onChange={(e) => atualizarOpcao(index, opcaoIndex, e.target.value)}
                                    />
                                    <button 
                                      className="btn btn-outline-danger"
                                      onClick={() => removerOpcao(index, opcaoIndex)}
                                      disabled={pergunta.opcoes.length <= 1}
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </div>
                                ))}
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => adicionarOpcao(index)}
                                >
                                  <i className="fas fa-plus me-2"></i>
                                  Adicionar Opção
                                </button>
                              </div>
                            )}

                            <div className="d-flex justify-content-end gap-2">
                              <button 
                                className="btn btn-secondary"
                                onClick={() => {
                                  if (!pergunta.id) {
                                    // Se é nova e não salvou, remove
                                    setPerguntas(perguntas.filter((_, i) => i !== index));
                                  }
                                  setPerguntaEditando(null);
                                }}
                              >
                                Cancelar
                              </button>
                              <button 
                                className="btn btn-primary"
                                onClick={() => salvarPergunta(index)}
                              >
                                <i className="fas fa-check me-2"></i>
                                Salvar Pergunta
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Modo visualização
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center mb-1">
                                <span className="badge bg-secondary me-2">{index + 1}</span>
                                <strong>{pergunta.titulo}</strong>
                                {pergunta.obrigatoria && (
                                  <span className="badge bg-danger ms-2">Obrigatória</span>
                                )}
                              </div>
                              <div className="d-flex align-items-center text-muted small">
                                <i className={`fas ${TIPOS_PERGUNTA.find(t => t.value === pergunta.tipo)?.icon} me-2`}></i>
                                {TIPOS_PERGUNTA.find(t => t.value === pergunta.tipo)?.label}
                                {pergunta.opcoes.length > 0 && (
                                  <span className="ms-2">
                                    ({pergunta.opcoes.length} opções)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="btn-group btn-group-sm">
                              <button 
                                className="btn btn-outline-primary"
                                onClick={() => setPerguntaEditando(index)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                className="btn btn-outline-danger"
                                onClick={() => excluirPergunta(index)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mensagem para salvar primeiro */}
          {!isEdicao && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Salve a pesquisa primeiro para poder adicionar perguntas.
            </div>
          )}

    </div>
  );
}

export default EditorPesquisa;
